import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule, formatDate } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';

// Serviços e modelos reais

import { ListaLogIntegracaoResponse } from '../../../shared/services/responses/integracao/lista-log-integracao.response';

import { LogIntegracaoStruct } from '../../../shared/services/structs/integracao/log-integracao.struct';
import { Uri } from '../../../shared/services/models/integracao/uri.model';
import { TipoLog } from '../../../shared/services/models/integracao/tipo-log.model';
import { LogIntegracaoService } from '../../../shared/services/apis/integracao/log-integracao.service';
import { IntegracaoLookupService } from '../../../shared/services/apis/integracao/integracao-lookup.service';
import { IntegracaoLookupResponse } from '../../../shared/services/responses/integracao/integracao-lookup.response';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { UtilService } from '../../../shared/services/util.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
    selector: 'app-log-integracao-lista',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        MatTableModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        EmptyListComponent,
        MatMenuModule,
        MatBadgeModule,
        HeaderComponent,
        LoadingBoxDefaultListComponent
    ],
    templateUrl: './log-integracao-lista.component.html',
    styleUrls: ['./log-integracao-lista.component.css']
})
export class LogIntegracaoListaComponent implements OnInit {
    public moduloEnum = ModuloEnum.Integracao;
    public funcionalidadeEnum = FuncionalidadeEnum.Integracao_LogIntegracao; // Ajuste conforme seu Enum
    public model: FormGroup;
    public _isLoading = false;
    public _isLookupLoading = false;
    public _ultimaPagina = false;
    public _paginaAtual = 0;
    public listaLogsResponse: ListaLogIntegracaoResponse | undefined;
    public _dataSource = new MatTableDataSource<LogIntegracaoStruct>();
    public displayedColumns: string[] = ['metodoUri', 'status', 'dataRequisicao', 'action-view'];

    public listaUri: Uri[] = [];
    public listaTipoLog: TipoLog[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private logIntegracaoService: LogIntegracaoService,
        private integrationLookupService: IntegracaoLookupService,
        private alertService: AlertService,
        private utilService: UtilService
    ) {
        this.model = this.formBuilder.group({
            idUri: [null],
            idTipoLog: [null],
            dataInicioRequest: [null],
            dataFimRequest: [null],
            textoBusca: ['']
        });
    }

    ngOnInit(): void {
        this.carregarLookups();
    }

    ngOnDestroy(): void {
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, ['/integracao/logs/detalhe']);
    }

    private loadFilters() {
        const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (filtroSalvo) {
            this.model.patchValue(filtroSalvo);
        }
    }

    carregarLookups(): void {
        this._isLookupLoading = true;
        this.integrationLookupService.getLookups().subscribe({
            next: (response: IntegracaoLookupResponse) => {
                if (response.isError) {
                    this.alertService.show('Erro ao buscar URIs', response.errorDescription, AlertType.error);
                    return;
                }

                this.listaUri = response.listaUri;
                this.listaTipoLog = response.listaTipoLog;
                this.loadFilters();
                this.pesquisar();
            },
            error: (err) => {
                this.alertService.show('Erro inesperado', err.message, AlertType.error)
            }
        });
    }

    clickPesquisar(): void {
        this._paginaAtual = 0;
        this.listaLogsResponse = undefined;
        this._dataSource.data = [];
        this.pesquisar();
    }

    pesquisar(): void {
        if (this._isLoading) return;

        this._isLoading = true;
        let idUri = this.model.get('idUri')?.value;
        let idTipoLog = this.model.get('idTipoLog')?.value;
        let dataInicioRequest = this.model.get('dataInicioRequest')?.value;
        let textoBusca = this.model.get('textoBusca')?.value;

        if (this.model.get('dataInicioRequest')?.value) {
            dataInicioRequest = formatDate(this.model.get('dataInicioRequest')?.value, 'yyyy-MM-dd', 'pt-BR');
        }

        this.logIntegracaoService.getLogs(idUri, idTipoLog, dataInicioRequest, this._paginaAtual, textoBusca).subscribe({
            next: (response: ListaLogIntegracaoResponse) => {
                this._isLoading = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription, AlertType.error);
                    return;
                }

                if (!this.listaLogsResponse) {
                    this.listaLogsResponse = response;
                } else {
                    this.listaLogsResponse.listaLogIntegracao.push(...response.listaLogIntegracao);
                }

                this._dataSource.data = this.listaLogsResponse.listaLogIntegracao;
                this._ultimaPagina = response.listaLogIntegracao.length < 10; // Ajuste a paginação conforme necessário
            },
            error: (error) => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', error.message, AlertType.error);
            }
        });
    }

    proximaPagina(): void {
        this._paginaAtual++;
        this.pesquisar();
    }

    limparFiltros(): void {
        this.model.reset();
        this._paginaAtual = 0;
        this.listaLogsResponse = undefined;
        this._dataSource.data = [];
        this.pesquisar();
    }

    get qtdFiltrosAvancados(): number {
        let count = 0;
        if (this.model.get('idTipoLog')?.value) count++;
        if (this.model.get('idUri')?.value) count++;
        if (this.model.get('dataInicioRequest')?.value) count++;
        return count;
    }
}